import React, {forwardRef, JSX, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import Select from "react-select";
import { colors, fonts, glyphs } from "../../Styles";
import { CreateNewFieldStyle, CreateNewFieldType, NewFieldStyles, NewFieldTypes, CreateListStyle, ListStyle_Types, SelectFieldOption_Values } from "../../Definitions";
import { components } from "react-select";
import { useTranslation } from "react-i18next";

/* eslint-disable */
export enum MenuFloats {
  left="left",
  right="right",
  center="center"
}
/* eslint-enable */

export interface NewSelectHandles {
  value: SelectFieldOption_Values;
  setValue: (e: SelectFieldOption_Values) => void;
  setError: (state?:boolean) => void
}

// #region | NewSelect
interface newSelectProps {
  options: SelectFieldOption_Values[]
  onChange?: (selectedOption: SelectFieldOption_Values | undefined) => void;
  onClear?: () => void
  placeholder?: string
  header?: string
  startEnhancer?: JSX.Element
  isRequired?: boolean
  isSearchable?: boolean
  isClearable?: boolean
  isDisabled?: boolean
  istableHeaderCell?: boolean
  fieldOverrides?: {
    fieldStyle?: NewFieldStyles
    fieldType?: NewFieldTypes
    fieldWidth?: string
    menuFloat?: MenuFloats
    menuWidth?: string
  }
  valueOverrides?: {
    defaultValue?: SelectFieldOption_Values
  }
}
/** NewSelect
 * @param options The list of selectable options.
 * @param onChange Callback function triggered when an option is selected.
 * @param placeholder The placeholder text for the select field.
 * @param header The header text displayed above the select field.
 * @param startEnhancer An optional icon or element displayed inside the select field.
 * @param isRequired Specifies whether the field is required.
 * @param isSearchable Enables search functionality within the select dropdown.
 * @param isClearable Allows clearing the selected option.
 * @param isDisabled Disables the select field.
 * @param fieldOverrides Custom styles and settings for the select field.
 * @param valueOverrides Default selected option.
 * @returns A styled dropdown react-select component.
 */
const _NewSelect: React.ForwardRefRenderFunction<NewSelectHandles, newSelectProps> = ({options, onChange, onClear, placeholder, header, startEnhancer, isRequired=false, isSearchable=false, isClearable=false, isDisabled=false, istableHeaderCell=false, fieldOverrides, valueOverrides}:newSelectProps, ref) => {
  const {t} = useTranslation()
  const [isClearHovered, setIsClearHovered] = useState(false);
  const [isContainerHovered, setIsContainerHovered] = useState<boolean>(false); 
  const [isError, setError] = useState(false)
  const [selectValue, setSelectValue] = useState<SelectFieldOption_Values | undefined>(valueOverrides?.defaultValue || undefined);
  
  const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  useImperativeHandle(ref, () => ({
    setError(state=true) {
      setError(state)
    },
    value: selectValue ?? ({} as SelectFieldOption_Values),
    setValue: (e: SelectFieldOption_Values) => { setSelectValue(e); }
  }));

  // Placeholder
  const _placeholder = placeholder ? placeholder + "..." : "...";

  // FieldStyle
  const { primaryColor, secondaryColor, fontColor, headerColor } = useMemo(() => {
    var _Style = CreateNewFieldStyle(fieldOverrides?.fieldStyle)
    return _Style
  }, [fieldOverrides?.fieldStyle])

  // FieldType
  const { height, containerPaddingTop, containerPaddingRight, containerPaddingBottom, containerPaddingLeft, indicatorPadding, indicatorSeparatorDisplay, dropdownIndicator, placeholderDisplay } = useMemo(() => {
    var _Style = CreateNewFieldType(fieldOverrides?.fieldType)
    return _Style
  }, [fieldOverrides?.fieldType])

  // Additional styling
  const _headerShadow = fieldOverrides&&fieldOverrides.fieldStyle === NewFieldStyles.greyTertiary ? undefined : {...fonts.textShadow} 
  const _fieldWidth = fieldOverrides&&fieldOverrides.fieldWidth !== undefined ? fieldOverrides.fieldWidth : "280px";
  
  // const _menuWidth = (fieldOverrides&&fieldOverrides.fieldType === NewFieldTypes.secondaryInputField) ? "280px" : _fieldWidth
  const _menuWidth = 
    fieldOverrides&&fieldOverrides.menuWidth ? fieldOverrides.menuWidth : _fieldWidth

  const _menuFloat = {
    right: (fieldOverrides&&fieldOverrides.menuFloat === MenuFloats.right) ? "0" : "unset",
    left: (fieldOverrides&&fieldOverrides.menuFloat === MenuFloats.left) ? "0" : (fieldOverrides&&fieldOverrides.menuFloat === MenuFloats.center) ? "50%" : "unset",
    transform: (fieldOverrides&&fieldOverrides.menuFloat === MenuFloats.center) ? "translateX(-50%)" : "unset"
  }

  const _containerPadding = (
    startEnhancer ? "2px 8px 2px 36px"
    : `${containerPaddingTop}px ${containerPaddingRight}px ${containerPaddingBottom}px ${containerPaddingLeft}px`
  )

  // Set value to defaultValue
  useEffect(() => {
    setSelectValue(valueOverrides?.defaultValue);
  }, [valueOverrides?.defaultValue]);

  // Handle option select change
  const handleChange = (selectedOption: SelectFieldOption_Values | null) => {
    setSelectValue(selectedOption ?? undefined);
    if (onChange) {
      onChange(selectedOption ?? undefined);
    }
  };
  // Ex. onChange={(e) => setSelectedTimeAmount(e?.id ?? 0)}

  const OptionWithIcon = (props: any) => {
    const { data, children, ...rest } = props;
    return (
      <components.Option {...rest}>
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {data.icon}
          {children}
        </span>
      </components.Option>
    );
  };

  const handleClear = () => {
    if (onClear && isClearable && selectValue !== undefined) {
      onClear();
    } else if (isClearable && selectValue !== undefined) {
      setSelectValue({ id: undefined, label: "" } as SelectFieldOption_Values);
      if (onChange) {
        onChange(undefined);
      }
    }
  };

  // #region | Clear Button
  const clearBtn = (
    <svg onClick={handleClear}
      onMouseEnter={() => setIsClearHovered(true)}
      onMouseLeave={() => setIsClearHovered(false)}
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 20 20"
      height="20"
      width="20"
      fill={isClearHovered ? colors.greyLightTertiary : secondaryColor}
      style={{
        transition: "fill 0.3s ease",
        cursor: "pointer",
        // padding: "8px",
      }}
    >
      <path d="M 14.348 14.849 c -0.469 0.469 -1.229 0.469 -1.697 0 l -2.651 -3.03 l -2.651 
        3.029 c -0.469 0.469 -1.229 0.469 -1.697 0 c -0.469 -0.469 -0.469 -1.229 0 -1.697 l 
        2.758 -3.15 l -2.759 -3.152 c -0.469 -0.469 -0.469 -1.228 0 -1.697 s 1.228 -0.469 
        1.697 0 l 2.652 3.031 l 2.651 -3.031 c 0.469 -0.469 1.228 -0.469 1.697 0 s 0.469 1.229 
        0 1.697 l -2.758 3.152 l 2.758 3.15 c 0.469 0.469 0.469 1.229 0 1.698 Z"
      />
    </svg>
  );
  // #endregion

  return ( 
    <div style={{width: _fieldWidth, cursor: isDisabled? "not-allowed" : "pointer"}}>
      {header && <p style={{ ..._headerShadow, ...fonts.header, ...fonts.fontSize4, color: headerColor, padding: "0px 0px 2px 10px"}}>{header}</p>}
      <div style={{position: "relative"}} onMouseEnter={() => istableHeaderCell&&setIsContainerHovered(true)} onMouseLeave={() => istableHeaderCell&&setIsContainerHovered(false)} >
        <Select<SelectFieldOption_Values>
          value={selectValue}
          onChange={handleChange} 
          defaultValue={valueOverrides?.defaultValue}
          placeholder={_placeholder}
          options={options} 
          required={isRequired}
          isSearchable={isSearchable}
          isClearable={isClearable}
          formatOptionLabel={(option, { context }) => (
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {capitalizeFirstLetter(option.label)}
            </span>
          )}
          isDisabled={isDisabled}
          menuPortalTarget={document.body} 
          maxMenuHeight={200}
          menuPlacement={"auto"}
          noOptionsMessage={() => t("noOptions", {defaultValue: "No Options"})}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => String(option.id ?? option.label)}
          components={{Option: OptionWithIcon}}
          styles={{
            container: (baseStyles) => ({
              ...baseStyles,
              width: _fieldWidth,
              height: `${height}px`,
              ...fonts.paragraph,
              ...fonts.fontSize4,
              zIndex: "99"
            }),
            control: (baseStyles) => ({
              ...baseStyles,
              backgroundColor: fieldOverrides&&fieldOverrides.fieldStyle === NewFieldStyles.transparent ? colors.transparent : isDisabled ? primaryColor+52 : primaryColor,
              border: "none",
              borderRadius: "10px",
              boxShadow: "none",
              cursor: "pointer",
              minHeight: "unset",
            }),
            valueContainer: (baseStyles) => ({
              ...baseStyles,
              padding: _containerPadding,
            }),
            input: (baseStyles) => ({
              ...baseStyles,
              color: fontColor,
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              ..._menuFloat,
              backgroundColor: primaryColor,
              borderRadius: "10px",
              width: _menuWidth,
            }),
            menuList: (baseStyles) => ({
              ...baseStyles,
              backgroundColor: primaryColor,
              borderRadius: "10px",
              padding: "10px",
              overflowY: "scroll",
              scrollbarWidth: "thin",
              scrollbarColor: secondaryColor+colors.transparent,
              ...fonts.paragraph,
              ...fonts.fontSize4,
            }),
            option: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: "10px",
              margin: "3px 0px",
              color: fontColor,
              backgroundColor: state.isSelected ? secondaryColor : primaryColor,
              transition: "background-color 0.3s ease",
              "&:hover": {
                backgroundColor: secondaryColor,
                cursor: "pointer"
              }
            }),
            singleValue: (baseStyles) => ({
              ...baseStyles,
              color: isDisabled ? fontColor+"ab" : fontColor,
            }),
            indicatorsContainer: (baseStyles) => ({
              ...baseStyles,
              height: `${height}px`,
            }), 
            indicatorSeparator: (baseStyles) => ({
              ...baseStyles,
              display: indicatorSeparatorDisplay,
              backgroundColor: isDisabled ? secondaryColor+"ab" : secondaryColor
            }), 
            dropdownIndicator: (baseStyles) => ({
              ...baseStyles,
              display: dropdownIndicator,
              padding: `${indicatorPadding}px`,
              color: isDisabled ? secondaryColor+"ab" : secondaryColor,
              "&:hover": {
                color: fontColor
              }
            }), 
            clearIndicator: (baseStyles) => ({
              ...baseStyles,
              padding: `${indicatorPadding}px`,
              color: secondaryColor,
              "&:hover": {
                color: fontColor
              }
            }), 
            placeholder: (baseStyles) => ({
              ...baseStyles,
              display: placeholderDisplay,
              color: fontColor,
              whiteSpace: "nowrap",
              filter: "opacity(0.5)",
              "::first-letter": {
                textTransform: "uppercase"
              }
            }),
            menuPortal: (baseStyles) => ({
              ...baseStyles,
              zIndex: "99999999999999"
            })
          }}
        />
        {istableHeaderCell ? 
          <div style={{position: "absolute", height: "20px", top: "50%", left: "50%", transform: "translate(-50%, -50%)", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", filter: isContainerHovered ? "opacity(0.3)" : "opacity(1)", transition: "filter 0.2s ease"}}>
            <glyphs.Add size="15px"/> 
          </div>
        : <></>}
        {startEnhancer && (
          <div style={{position: "absolute", height: "20px", top: "50%", transform: "translateY(-50%)", left: "8px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center"}}>
            {startEnhancer}
          </div>
        )}
        {isRequired &&(
          <div style={{ position: "absolute", height: "22px", top: "50%", right: "-30px", transform: "translateY(-50%)", display: "grid", gridAutoFlow: "column", gridAutoColumns: "1fr", padding: "4px 8px", alignItems: "center" }}>
            <glyphs.Error fillColor={colors.systemRed} />
          </div>
        )}
        <div style={{ position: "absolute", height: "22px", top: "50%", transform: "translateY(-50%)", right: "37px", display: "grid", gridAutoFlow: "column", gridAutoColumns: "1fr", padding: "4px 8px", alignItems: "center" }}>
          {isClearable ? (<>{clearBtn}</>) : <></>}
        </div>
      </div>
    </div> 
  )
}
const NewSelect = forwardRef(_NewSelect);
export default NewSelect
// #endregion

// #region | NewSelectList
export interface NewSelectListHandles {
  value: (index: number) => SelectFieldOption_Values | undefined;
  setError: (index: number, state?:boolean) => void
  refs: React.MutableRefObject<(NewSelectHandles | null)[]>;
}

interface newSelectListProps {
  options: SelectFieldOption_Values[][]
  onChanges?: ((selectedOption: SelectFieldOption_Values | undefined) => void)[]
  placeholders: string[]
  headers?: string[]
  startEnhancers?: JSX.Element[]
  isRequired?: boolean[]
  isSearchable?: boolean
  isClearable?: boolean
  isDisabled?: boolean[]
  fieldOverrides?: {
    fieldStyle?: NewFieldStyles
    fieldType?: NewFieldTypes
    fieldWidth?: string
    menuFloat?: MenuFloats
  }
  valueOverrides?: {
    defaultValues?: SelectFieldOption_Values[]
  }
  listOverrides?: {
    listGap?: string
    listStyle?: ListStyle_Types
  }
}
/**
 * NewSelectList
 * @param options A list of selectable options for each select field.
 * @param onChanges Callback functions triggered when an option is selected for each select field.
 * @param placeholders Placeholder texts for each select field.
 * @param headers Header texts displayed above each select field.
 * @param startEnhancers Icons or elements displayed inside each select field.
 * @param isRequired Specifies whether each field is required.
 * @param isSearchable Enables search functionality within all select fields.
 * @param isClearable Allows clearing selected options in all fields.
 * @param isDisabled Disables the select fields.
 * @param fieldOverrides Custom styles and settings for each select field.
 * @param valueOverrides Default selected values for each select field.
 * @param listOverrides Custom styles and settings for the list layout.
 * @param ref A reference for accessing input values and elements.
 * @returns A list of styled dropdown react-select components. 
 */
const _NewSelectList:React.ForwardRefRenderFunction<NewSelectListHandles, newSelectListProps> = ({options, onChanges, placeholders, headers, startEnhancers, isRequired=[false], isSearchable=false, isClearable=false, isDisabled=[false], fieldOverrides, valueOverrides, listOverrides}:newSelectListProps, ref) => {
  const selectRefs = useRef<(NewSelectHandles | null)[]>([]);
  const _listGap = listOverrides&&listOverrides.listGap ? { gap: listOverrides.listGap } : { gap: "5px" };
  const { listDirection } = useMemo(() => CreateListStyle(listOverrides&&listOverrides.listStyle), [listOverrides])

  useImperativeHandle(ref, ()=>({
    setError(index=0,state) { 
      if (selectRefs.current && selectRefs.current[index]) selectRefs.current[index].setError(state)
    },
    value(index=0): SelectFieldOption_Values | undefined {
      if (selectRefs.current && selectRefs.current[index]) return selectRefs.current[index]?.value
      return undefined
    },
    refs:selectRefs
  }))

  const Item_List = useMemo(() => {
    return placeholders?.map((_placeholders, index) => {
      const _options = options && options[index] ? options[index] : []
      const _onChanges = onChanges && onChanges[index] ? onChanges[index] : undefined
      const _headers = headers&&headers[index] ? headers[index] : ""
      const _defaultValues = valueOverrides&&valueOverrides.defaultValues && valueOverrides.defaultValues[index] ? valueOverrides.defaultValues[index] : undefined
      const _startEnhancers = startEnhancers&&startEnhancers[index] ? startEnhancers[index] : undefined
      const _isRequired = isRequired && isRequired[index] ? isRequired[index] : undefined
      const _isDisabled = isDisabled && isDisabled[index] ? isDisabled[index] : undefined
      const _overrides = fieldOverrides || {};

      var view = [
        <NewSelect 
          key={"List_Select_" + index}
          ref={(el) => el ? selectRefs.current[index] = el : null}
          placeholder={_placeholders}
          options={_options}     
          onChange={_onChanges}
          header={_headers}
          startEnhancer={_startEnhancers}
          isRequired={_isRequired}
          isSearchable={isSearchable}
          isClearable={isClearable}
          isDisabled={_isDisabled}
          fieldOverrides={_overrides}
          valueOverrides={{
            defaultValue: _defaultValues
          }}
        />
      ];

      return view;
    });
  }, [placeholders, options, onChanges, headers, valueOverrides, startEnhancers, isRequired, isDisabled, fieldOverrides, isSearchable, isClearable]);

  return (
    <div style={{...listDirection as React.CSSProperties, ..._listGap}}>
      {Item_List}
    </div>
  ) 
}
export const NewSelectList = forwardRef(_NewSelectList)
// #endregion